import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import Tooltip from '@mui/material/Tooltip'
import { Link } from 'react-router'
import { CiLocationOn, CiClock2 } from 'react-icons/ci'
import { BsDot } from 'react-icons/bs'
import { PiPhoneCallThin } from 'react-icons/pi'
import { IoShareSocialOutline } from 'react-icons/io5'
import { FaInstagram, FaLinkedinIn, FaSquareXTwitter, FaWhatsapp } from 'react-icons/fa6'
import { RiTiktokLine } from 'react-icons/ri'
import { sendContactEmails } from '../utils/contactService'

const serviceOptions = [
    'Demo',
    'Virtual Tours',
    'Virtual Reality',
    'Augmented Reality',
    'Web Application Development',
    'Tour Visit',
    'Consultation',
    'Support',
    'Others'
]

const contactCards = [
    {
        title: 'Physical address',
        icon: <CiLocationOn className="text-2xl text-yellow-300" />,
        items: ['Nii Sai Road, East Legon, Accra.']
    },
    {
        title: 'Opening hours',
        icon: <CiClock2 className="text-2xl text-yellow-300" />,
        items: ['Monday - Friday: 09:00 - 19:00 GMT', 'Saturday: 09:00 - 19:00 GMT', 'Sunday: Closed']
    },
    {
        title: 'General enquiries',
        icon: <PiPhoneCallThin className="text-2xl text-yellow-300" />,
        items: ['Email: info@vrifytechnologies.com', 'Email: business@vrifytechnologies.com','Email: support@vrifytechnologies.com',  'Phone: +233 544847395','Phone: +233 241145862', 'Phone: +233 553139500']
    }
]

const socialLinks = [
    { title: 'LinkedIn', href: 'https://www.linkedin.com/company/vrify-technologies', icon: <FaLinkedinIn className="text-lg" /> },
    { title: 'X (formerly Twitter)', href: 'https://x.com/VrifyTech?t=WcnaecQOz1HMkjra3Bpd7A&s=08', icon: <FaSquareXTwitter className="text-lg" /> },
    { title: 'Instagram', href: 'https://www.instagram.com/vrifytechnologies?igsh=MTdpbWl4eGFibjVmbA=', icon: <FaInstagram className="text-lg" /> },
    { title: 'WhatsApp', href: 'https://wa.me/c/233544847395', icon: <FaWhatsapp className="text-lg" /> },
    { title: 'TikTok', href: 'https://www.tiktok.com/@vrifytechnologies?_t=ZM-90BY6Oc2wHr&_r=1', icon: <RiTiktokLine className="text-lg" /> }
]

const Contact = () => {
    const [formState, setFormState] = useState({
        fullname: '',
        companyName: '',
        email: '',
        phone: '',
        services: '',
        message: '',
        website: ''
    })
    const [submitting, setSubmitting] = useState(false)
    const [status, setStatus] = useState({ type: '', message: '' })

    const handleChange = (event) => {
        const { name, value } = event.target
        setFormState((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        setStatus({ type: '', message: '' })

        const { fullname, email, phone, services, message } = formState
        if (!fullname.trim() || !email.trim() || !phone.trim() || !services.trim() || !message.trim()) {
            setStatus({ type: 'error', message: 'Please complete all required fields before sending.' })
            return
        }

        setSubmitting(true)
        try {
            await sendContactEmails(formState)
            setStatus({ type: 'success', message: 'Your enquiry has been submitted successfully. We will reach out shortly.' })
            setFormState({ fullname: '', companyName: '', email: '', phone: '', services: '', message: '', website: '' })
        } catch (error) {
            console.error(error)
            setStatus({ type: 'error', message: error?.message || 'An unexpected error occurred while sending your enquiry.' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div>
            <Helmet>
                <title>Contact Us | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Get in touch with VRIFY Technologies. Contact us for VR, AR, and Virtual Tour solutions for your business."
                />
                <meta
                    name="keywords"
                    content="contact, VRIFY, VR solutions, AR solutions, virtual tours, Africa, VRIFY Technologies, tourism, ghana, tourism in africa, tourism in ghana"
                />
                <meta property="og:title" content="Contact Us | VRIFY Technologies" />
                <meta property="og:description" content="Get in touch with VRIFY Technologies for immersive VR and AR solutions." />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.25),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_22%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8 lg:pb-10">
                        <ScrollReveal>
                            <div className="max-w-3xl">
                                <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                    <IoShareSocialOutline className="text-sm" />
                                    Contact us
                                </p>
                                <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Let's build a remarkable immersive experience together.
                                </h1>
                                <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                    Share your goals, timeline, and budget and our team will help shape a solution that fits your audience and your brand.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
                            <div className="space-y-4">
                                {contactCards.map((card) => (
                                    <ScrollReveal key={card.title}>
                                        <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm sm:p-6">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded-2xl bg-slate-900/85 p-3">
                                                    {card.icon}
                                                </div>
                                                <div className="flex-1">
                                                    <h2 className="text-lg font-semibold text-white">{card.title}</h2>
                                                    <div className="mt-3 space-y-2">
                                                        {card.items.map((item) => (
                                                            <div key={item} className="flex items-start gap-2 text-sm text-slate-200">
                                                                <BsDot className="mt-1 shrink-0 text-lg text-yellow-300" />
                                                                <span>{item}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </ScrollReveal>
                                ))}

                                <ScrollReveal>
                                    <div className="rounded-[24px] border border-white/10 bg-gradient-to-br from-yellow-400 via-amber-300 to-yellow-200 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <div className="flex items-center gap-3">
                                                <IoShareSocialOutline className="text-2xl text-yellow-300" />
                                                <div>
                                                    <h2 className="text-lg font-semibold text-white">Social handles</h2>
                                                    <p className="mt-1 text-sm text-slate-300">Follow updates, behind-the-scenes moments, and campaign highlights.</p>
                                                </div>
                                            </div>
                                            <div className="mt-4 flex flex-wrap gap-3">
                                                {socialLinks.map((item) => (
                                                    <Tooltip title={item.title} arrow key={item.title}>
                                                        <Link
                                                            to={item.href}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                                        >
                                                            {item.icon}
                                                        </Link>
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </ScrollReveal>
                            </div>

                            <ScrollReveal>
                                <form
                                    onSubmit={handleSubmit}
                                    className='relative rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.45)] backdrop-blur-sm sm:p-8'
                                >
                                    <div className='absolute -left-[10000px] h-px w-px overflow-hidden' aria-hidden='true'>
                                        <label htmlFor='website'>Website</label>
                                        <input
                                            id='website'
                                            type='text'
                                            name='website'
                                            value={formState.website}
                                            onChange={handleChange}
                                            tabIndex='-1'
                                            autoComplete='off'
                                        />
                                    </div>
                                    <p className='text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300'>Contact form</p>
                                    <h2 className='mt-3 text-2xl font-bold text-white sm:text-3xl'>Send us a message</h2>
                                    <p className='mt-3 text-sm leading-7 text-slate-300'>
                                        We would like to hear from you. Please send us a message by filling out the form below and we will get back with you shortly.
                                    </p>

                                    <div className='mt-6 grid gap-4 sm:grid-cols-2'>
                                        <div className='flex flex-col'>
                                            <label htmlFor='fullname' className='text-sm font-medium text-slate-100'>Full name <span className='text-red-400'>*</span></label>
                                            <input
                                                id='fullname'
                                                type='text'
                                                placeholder='Eg. Kofi Mensah'
                                                name='fullname'
                                                value={formState.fullname}
                                                onChange={handleChange}
                                                maxLength={120}
                                                className='mt-2 rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus:border-yellow-300'
                                                autoComplete='on'
                                                required
                                            />
                                        </div>

                                        <div className='flex flex-col'>
                                            <label htmlFor='companyName' className='text-sm font-medium text-slate-100'>Company name</label>
                                            <input
                                                id='companyName'
                                                type='text'
                                                placeholder='Eg. xyz company'
                                                name='companyName'
                                                value={formState.companyName}
                                                onChange={handleChange}
                                                maxLength={160}
                                                className='mt-2 rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus:border-yellow-300'
                                                autoComplete='on'
                                            />
                                        </div>

                                        <div className='flex flex-col'>
                                            <label htmlFor='email' className='text-sm font-medium text-slate-100'>Email <span className='text-red-400'>*</span></label>
                                            <input
                                                id='email'
                                                type='email'
                                                placeholder='Eg. kofimensah@xyzcompany.com'
                                                name='email'
                                                value={formState.email}
                                                onChange={handleChange}
                                                maxLength={254}
                                                className='mt-2 rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus:border-yellow-300'
                                                autoComplete='on'
                                                required
                                            />
                                        </div>

                                        <div className='flex flex-col'>
                                            <label htmlFor='phone' className='text-sm font-medium text-slate-100'>Phone <span className='text-red-400'>*</span></label>
                                            <input
                                                id='phone'
                                                type='tel'
                                                placeholder='233 00000000'
                                                name='phone'
                                                value={formState.phone}
                                                onChange={handleChange}
                                                className='mt-2 rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus:border-yellow-300'
                                                autoComplete='on'
                                                maxLength={10}
                                                pattern='[0-9]*'
                                                inputMode='numeric'
                                                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, '') }}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className='mt-4 flex flex-col'>
                                        <label htmlFor='services' className='text-sm font-medium text-slate-100'>Services <span className='text-red-400'>*</span></label>
                                        <select
                                            id='services'
                                            name='services'
                                            value={formState.services}
                                            onChange={handleChange}
                                            className='mt-2 rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-yellow-300'
                                            required
                                        >
                                            <option value='' disabled>Select a service</option>
                                            {serviceOptions.map((option) => (
                                                <option key={option.toLowerCase()} value={option.toLowerCase()}>{option}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className='mt-4 flex flex-col'>
                                        <label htmlFor='message' className='text-sm font-medium text-slate-100'>Message <span className='text-red-400'>*</span></label>
                                        <textarea
                                            id='message'
                                            name='message'
                                            placeholder='How can we help you? Feel free to leave a message.'
                                            value={formState.message}
                                            onChange={handleChange}
                                            maxLength={5000}
                                            className='mt-2 min-h-[140px] rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus:border-yellow-300'
                                            required
                                        />
                                    </div>

                                    <div className='mt-5 flex items-start gap-3 rounded-2xl border border-yellow-300/30 bg-yellow-300/10 px-4 py-3'>
                                        <input type='checkbox' name='checkbox' className='mt-1 accent-yellow-400' required />
                                        <label htmlFor='checkbox' className='text-sm text-slate-200'>
                                            I have read and agreed to the
                                            {' '}
                                            <a href='/data-processing-policy' className='font-semibold text-yellow-200 underline-offset-2 hover:underline' target='_blank' rel='noreferrer noopener'>
                                                data processing policy.
                                            </a>
                                        </label>
                                    </div>

                                    {status.message && (
                                        <div className={`mt-4 rounded-2xl px-4 py-3 ${status.type === 'success' ? 'bg-emerald-500/15 border border-emerald-400/30 text-emerald-200' : 'bg-rose-500/15 border border-rose-400/30 text-rose-200'}`}>
                                            {status.message}
                                        </div>
                                    )}

                                    <div className='mt-8 flex justify-end'>
                                        <button
                                            type='submit'
                                            disabled={submitting}
                                            className='inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-60'
                                        >
                                            {submitting ? 'Sending...' : 'Submit enquiry'}
                                        </button>
                                    </div>
                                </form>
                            </ScrollReveal>
                        </div>
                    </section>

                    <section className='mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8'>
                        <ScrollReveal>
                            <div className='rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10'>
                                <div className='max-w-2xl'>
                                    <p className='text-xs font-semibold uppercase tracking-[0.3em] text-slate-950'>Fast response</p>
                                    <h2 className='mt-3 text-2xl font-bold text-slate-950 sm:text-3xl'>We respond quickly, so your next idea stays in motion.</h2>
                                    <p className='mt-3 text-sm leading-7 text-slate-900/80 sm:text-base'>
                                        Every enquiry is reviewed by the team and followed up within one to two business days.
                                    </p>
                                </div>
                                <div className='mt-6 flex flex-wrap gap-3 lg:mt-0'>
                                    <a href='/virtual-tours' className='inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900'>
                                        Explore services
                                    </a>
                                    {/* <a href='/' className='inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300'>
                                        Back home
                                    </a> */}
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default Contact
